import { useState, useEffect, useCallback } from 'react';
import { Capacitor } from '@capacitor/core';
import { supabase } from '@/lib/supabase';
// Bridge natif partagé (registerPlugin, proxy synchrone) — cf. le pourquoi dans
// `@/lib/notifications/firebase-messaging`. Le tap sur notification (deep link)
// est écouté globalement dans `main.tsx`, pas ici.
import { FirebaseMessaging } from '@/lib/notifications/firebase-messaging';

// Garde anti-blocage : si un appel natif ne répond jamais (ex. APNs mal
// configuré), on ne laisse pas le spinner tourner à l'infini — on lève.
function withTimeout<T>(p: Promise<T>, ms: number, label: string): Promise<T> {
  return Promise.race([
    p,
    new Promise<T>((_, reject) =>
      setTimeout(() => reject(new Error(`${label} : délai dépassé (${ms / 1000}s)`)), ms),
    ),
  ]);
}

const VAPID_PUBLIC_KEY = import.meta.env.VITE_VAPID_PUBLIC_KEY as string | undefined;
const SUPABASE_URL = import.meta.env.VITE_SUPABASE_URL as string;

const isNative = Capacitor.isNativePlatform();

// Logs de diagnostic — visibles dans Safari Web Inspector (device branché) et,
// en build debug, dans la console Xcode (Capacitor relaie console.*). Préfixe
// [push] pour filtrer facilement. On tronque les tokens (secrets).
function pushLog(msg: string, extra?: unknown) {
  if (extra === undefined) console.log(`[push] ${msg}`);
  else console.log(`[push] ${msg}`, extra);
}
function pushErr(msg: string, err: unknown) {
  console.error(`[push] ${msg}`, err);
}
function tokenPreview(token: string | undefined | null): string {
  if (!token) return '(vide)';
  return `${token.slice(0, 10)}…(${token.length})`;
}

function urlBase64ToUint8Array(base64String: string): ArrayBuffer {
  const padding = '='.repeat((4 - (base64String.length % 4)) % 4);
  const base64 = (base64String + padding).replace(/-/g, '+').replace(/_/g, '/');
  const rawData = atob(base64);
  const arr = new Uint8Array(rawData.length);
  for (let i = 0; i < rawData.length; i++) arr[i] = rawData.charCodeAt(i);
  return arr.buffer;
}

function extractKeys(sub: PushSubscription): { p256dh: string; auth: string } {
  const p256dhBuf = sub.getKey('p256dh');
  const authBuf = sub.getKey('auth');
  if (!p256dhBuf || !authBuf) throw new Error('Clés push manquantes');
  return {
    p256dh: btoa(String.fromCharCode(...new Uint8Array(p256dhBuf))),
    auth: btoa(String.fromCharCode(...new Uint8Array(authBuf))),
  };
}

async function postToFunction(path: 'push-subscribe' | 'push-unsubscribe', body: unknown) {
  const {
    data: { session },
  } = await supabase.auth.getSession();
  if (!session) throw new Error('Non connecté');
  const res = await fetch(`${SUPABASE_URL}/functions/v1/${path}`, {
    method: 'POST',
    headers: {
      'Content-Type': 'application/json',
      Authorization: `Bearer ${session.access_token}`,
    },
    body: JSON.stringify(body),
  });
  if (!res.ok) {
    const err = await res.json().catch(() => ({}));
    throw new Error((err as { error?: string }).error ?? 'Erreur serveur');
  }
}

export type PushState = 'unsupported' | 'denied' | 'subscribed' | 'unsubscribed' | 'loading';

/**
 * Notifications push — web (Web Push/VAPID) ET app mobile Capacitor (FCM),
 * derrière la même interface. `isNative` est figé pour la durée du process
 * (déterminé par la plateforme, jamais par un état applicatif), donc brancher
 * dessus à l'intérieur des hooks ci-dessous ne viole pas les rules-of-hooks —
 * chaque useState/useEffect/useCallback est toujours appelé, seule la logique
 * interne diffère. Cf. Phase 3 de CAPACITOR_MOBILE_PLAN.md.
 */
export function usePushNotifications() {
  const [state, setState] = useState<PushState>('loading');
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    pushLog(`init (isNative=${isNative}, platform=${Capacitor.getPlatform()})`);
    if (isNative) {
      let cancelled = false;

      withTimeout(FirebaseMessaging.checkPermissions(), 10000, 'checkPermissions')
        .then((p) => {
          if (cancelled) return;
          pushLog(`checkPermissions → ${p.receive}`);
          if (p.receive === 'denied') setState('denied');
          else if (p.receive === 'granted') setState('subscribed');
          else setState('unsubscribed');
        })
        .catch((e: unknown) => {
          pushErr('checkPermissions a échoué', e);
          // Ne JAMAIS rester bloqué sur "loading" (bouton désactivé) : on
          // repasse en "unsubscribed" pour que le tap déclenche le vrai flow.
          if (!cancelled) setState('unsubscribed');
        });

      // La rotation du token FCM est écoutée GLOBALEMENT au démarrage
      // (`watchNativePushToken`, main.tsx) : elle doit marcher quel que soit
      // l'écran affiché, pas seulement quand « Mon profil » est monté.
      return () => {
        cancelled = true;
      };
    }

    if (!('serviceWorker' in navigator) || !('PushManager' in window)) {
      setState('unsupported');
      return undefined;
    }
    if (Notification.permission === 'denied') {
      setState('denied');
      return undefined;
    }
    navigator.serviceWorker.ready.then((reg) => {
      reg.pushManager.getSubscription().then((sub) => {
        setState(sub ? 'subscribed' : 'unsubscribed');
      });
    });
    return undefined;
  }, []);

  const subscribe = useCallback(async () => {
    pushLog(`subscribe: tap (isNative=${isNative})`);
    setIsLoading(true);
    setError(null);
    try {
      if (isNative) {
        pushLog('subscribe: requestPermissions…');
        const perm = await FirebaseMessaging.requestPermissions();
        pushLog(`subscribe: requestPermissions → ${perm.receive}`);
        if (perm.receive !== 'granted') {
          setState('denied');
          setError('Notifications refusées.');
          return;
        }
        pushLog('subscribe: getToken…');
        const { token } = await withTimeout(FirebaseMessaging.getToken(), 20000, 'getToken');
        pushLog(`subscribe: getToken → ${tokenPreview(token)}`);
        await postToFunction('push-subscribe', { platform: Capacitor.getPlatform(), token });
        pushLog('subscribe: push-subscribe OK');
        setState('subscribed');
        return;
      }

      if (!VAPID_PUBLIC_KEY) {
        setError('Notifications push non configurées.');
        return;
      }
      const reg = await navigator.serviceWorker.ready;
      const sub = await reg.pushManager.subscribe({
        userVisibleOnly: true,
        applicationServerKey: urlBase64ToUint8Array(VAPID_PUBLIC_KEY),
      });
      const keys = extractKeys(sub);
      await postToFunction('push-subscribe', { endpoint: sub.endpoint, keys });
      pushLog('subscribe: push-subscribe OK (web)');
      setState('subscribed');
    } catch (e) {
      pushErr('subscribe a échoué', e);
      if (!isNative && Notification.permission === 'denied') {
        setState('denied');
        setError('Notifications bloquées dans les réglages du navigateur.');
      } else {
        setError(e instanceof Error ? e.message : 'Erreur inconnue');
      }
    } finally {
      setIsLoading(false);
    }
  }, []);

  const unsubscribe = useCallback(async () => {
    pushLog(`unsubscribe: tap (isNative=${isNative})`);
    setIsLoading(true);
    setError(null);
    try {
      if (isNative) {
        const { token } = await withTimeout(FirebaseMessaging.getToken(), 20000, 'getToken');
        pushLog(`unsubscribe: getToken → ${tokenPreview(token)}`);
        await postToFunction('push-unsubscribe', { endpoint: token });
        await FirebaseMessaging.deleteToken();
        pushLog('unsubscribe: OK');
        setState('unsubscribed');
        return;
      }

      const reg = await navigator.serviceWorker.ready;
      const sub = await reg.pushManager.getSubscription();
      if (sub) {
        await postToFunction('push-unsubscribe', { endpoint: sub.endpoint }).catch(() => {
          /* best-effort : on désabonne quand même localement */
        });
        await sub.unsubscribe();
      }
      setState('unsubscribed');
    } catch (e) {
      pushErr('unsubscribe a échoué', e);
      setError(e instanceof Error ? e.message : 'Erreur inconnue');
    } finally {
      setIsLoading(false);
    }
  }, []);

  return { state, isLoading, error, subscribe, unsubscribe };
}
